import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, House, Briefcase, MapPin, CurrencyDollar, Clock, CheckCircle,
  Circle, ArrowsClockwise, PaperPlaneTilt, Camera, CaretDown, CaretUp,
  Flag, Image, ChatCircleDots, ChatCircle, Hammer, Play, Pause, Star, Plus, X,
  ShieldCheck, Wallet, CreditCard, Bank, DownloadSimple, ShareNetwork, Copy, LinkSimple, Heart, SealCheck
} from '@phosphor-icons/react';
import { toast } from '../hooks/use-toast';
import { ReviewModal } from '../components/homeowner/ReviewModal';
import { Lightbox } from '../components/Lightbox';
import { LOGO_URL } from '../config/branding';
import { openCheckout } from '../utils/checkout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  planning: { bg: 'bg-blue-500', text: 'text-blue-700', light: 'bg-blue-50 dark:bg-blue-900/20', label: 'Planning' },
  in_progress: { bg: 'bg-amber-500', text: 'text-amber-700', light: 'bg-amber-50 dark:bg-amber-900/20', label: 'In Progress' },
  completed: { bg: 'bg-emerald-500', text: 'text-emerald-700', light: 'bg-emerald-50 dark:bg-emerald-900/20', label: 'Completed' },
  on_hold: { bg: 'bg-neutral-500', text: 'text-neutral-600', light: 'bg-neutral-100 dark:bg-neutral-800', label: 'On Hold' }
};

const MS_STATUS = {
  pending: { icon: Circle, color: 'text-neutral-400', line: 'bg-neutral-300 dark:bg-neutral-600' },
  in_progress: { icon: ArrowsClockwise, color: 'text-amber-500', line: 'bg-amber-400' },
  completed: { icon: CheckCircle, color: 'text-emerald-500', line: 'bg-emerald-400' }
};

export default function ProjectTracker() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [newUpdate, setNewUpdate] = useState('');
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoData, setPhotoData] = useState(null);
  const [posting, setPosting] = useState(false);
  const [userType, setUserType] = useState(null);
  const [userId, setUserId] = useState(null);
  const [funding, setFunding] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [partnerInfo, setPartnerInfo] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [shareUrl, setShareUrl] = useState('');
  const [shareOpen, setShareOpen] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [chatSending, setChatSending] = useState(false);
  const [lightboxImages, setLightboxImages] = useState(null);
  const [commentInputs, setCommentInputs] = useState({});
  const [openThread, setOpenThread] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showReviewNudge, setShowReviewNudge] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const chatScrollRef = React.useRef(null);
  const chatPollRef = React.useRef(null);

  useEffect(() => {
    setUserType(localStorage.getItem('userType'));
    setUserId(localStorage.getItem('userId'));
  }, []);

  const fetchProject = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/project-tracker/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.status === 401) { navigate('/login'); return; }
      if (!res.ok) { navigate(-1); return; }
      const data = await res.json();
      if (data.success) {
        setProject(data.project);
        setMilestones(data.milestones);
        setUpdates(data.updates);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [projectId, navigate]);

  useEffect(() => { fetchProject(); }, [fetchProject]);

  // Auto-confirm funding on redirect from Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('funded') === 'true') {
      (async () => {
        try {
          const token = localStorage.getItem('token');
          await fetch(`${API_URL}/api/project-tracker/${projectId}/fund/confirm`, {
            method: 'POST', headers: { Authorization: `Bearer ${token}` }
          });
          await fetchProject();
          toast({ title: 'Project Funded!', description: 'Escrow is now active. Payments will release as milestones complete.' });
        } catch { /* ignore */ }
        window.history.replaceState({}, '', `/project/${projectId}`);
      })();
    }
  }, [projectId, fetchProject]);

  const handleFundProject = async () => {
    setFunding(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/project-tracker/${projectId}/fund`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success && data.checkout_url) {
        openCheckout(data.checkout_url);
      } else {
        toast({ title: 'Error', description: data.detail || 'Could not start payment', variant: 'destructive' });
      }
    } catch { toast({ title: 'Error', description: 'Network error', variant: 'destructive' }); }
    finally { setFunding(false); }
  };

  const handleToggleMilestone = async (milestoneId, currentStatus) => {
    const newStatus = currentStatus === 'completed' ? 'pending' : currentStatus === 'in_progress' ? 'completed' : 'in_progress';
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/project-tracker/${projectId}/milestones/${milestoneId}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      if (res.ok) { await fetchProject(); }
    } catch { /* ignore */ }
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast({ title: 'File too large', description: 'Max 5MB', variant: 'destructive' }); return; }
    const reader = new FileReader();
    reader.onload = (ev) => { setPhotoData(ev.target.result); setPhotoPreview(URL.createObjectURL(file)); };
    reader.readAsDataURL(file);
  };

  const handlePostUpdate = async () => {
    if (!newUpdate.trim()) return;
    setPosting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/project-tracker/${projectId}/updates`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_id: projectId, message: newUpdate, photo_data: photoData })
      });
      if (res.ok) {
        setNewUpdate(''); setPhotoData(null); setPhotoPreview(null);
        await fetchProject();
        toast({ title: 'Update posted' });
      }
    } catch { toast({ title: 'Error posting update', variant: 'destructive' }); }
    finally { setPosting(false); }
  };

  const handleStatusToggle = async () => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/project-tracker/${projectId}/status`, {
        method: 'PUT', headers: { Authorization: `Bearer ${token}` }
      });
      await fetchProject();
    } catch { /* ignore */ }
  };

  // Messaging to the other party
  const getPartnerId = () => {
    if (!project) return null;
    return userType === 'homeowner' ? project.contractor_id : project.homeowner_id;
  };

  const openShare = async () => {
    setShareOpen(true);
    if (shareUrl) return;
    setSharing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/project-tracker/${projectId}/share`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setShareUrl(`${window.location.origin}${data.path}`);
      else toast({ title: data.detail || 'Could not create link', variant: 'destructive' });
    } catch {
      toast({ title: 'Could not create share link', variant: 'destructive' });
    } finally {
      setSharing(false);
    }
  };

  const copyShare = () => {
    navigator.clipboard.writeText(shareUrl);
    toast({ title: 'Link copied', description: 'Anyone with this link can view progress (read-only).' });
  };

  const approveMilestone = async (milestoneId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/project-tracker/${projectId}/milestones/${milestoneId}/approve`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const r = await res.json();
      if (r.success) {
        setMilestones((prev) => prev.map((m) => m.id === milestoneId
          ? { ...m, approved: true, approved_by_name: r.approved_by_name, approved_at: r.approved_at } : m));
        if (!r.already_approved) {
          toast({
            title: 'Milestone approved ✓',
            description: r.payment_released
              ? `$${Number(r.release_amount).toLocaleString()} released from escrow to the contractor's wallet.`
              : 'A timestamped approval record was added to the timeline.',
          });
          fetchProject();
          // Highest-intent moment to ask for a review — but only if not already reviewed
          if (userType === 'homeowner' && project?.contractor_id) {
            try {
              const token = localStorage.getItem('token');
              const cr = await fetch(`${API_URL}/api/reviews/can-review/${project.contractor_id}`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              const crData = await cr.json();
              if (crData?.can_review) setShowReviewNudge(true);
            } catch { /* no-op */ }
          }
        }
      } else {
        toast({ title: r.detail || 'Could not approve', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Could not approve milestone', variant: 'destructive' });
    }
  };

  const submitComment = async (updateId) => {    const text = (commentInputs[updateId] || '').trim();
    if (!text) return;
    setCommentInputs((p) => ({ ...p, [updateId]: '' }));
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/project-tracker/${projectId}/updates/${updateId}/comments`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
      });
      const r = await res.json();
      if (r.success) {
        setUpdates((prev) => prev.map((u) => u.id === updateId
          ? { ...u, comments: [...(u.comments || []), r.comment] } : u));
      }
    } catch {
      setCommentInputs((p) => ({ ...p, [updateId]: text }));
    }
  };

  const toggleLove = async (updateId) => {    // optimistic
    setUpdates((prev) => prev.map((u) => u.id === updateId
      ? { ...u, loved: !u.loved, loved_count: (u.loved_count || 0) + (u.loved ? -1 : 1) }
      : u));
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/project-tracker/${projectId}/updates/${updateId}/react`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setUpdates((prev) => prev.map((u) => u.id === updateId
          ? { ...u, loved: data.loved, loved_count: data.loved_count } : u));
      }
    } catch {
      // revert on failure
      setUpdates((prev) => prev.map((u) => u.id === updateId
        ? { ...u, loved: !u.loved, loved_count: (u.loved_count || 0) + (u.loved ? -1 : 1) }
        : u));
    }
  };

  const fetchChatMessages = useCallback(async () => {
    const pid = getPartnerId();
    if (!pid) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/messages/${pid}?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setChatMessages(data.messages || []);
          if (data.partner) setPartnerInfo(data.partner);
        }
      }
    } catch { /* ignore */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only effect
  }, [project, userType]);

  useEffect(() => {
    if (chatOpen) {
      fetchChatMessages();
      chatPollRef.current = setInterval(fetchChatMessages, 5000);
      return () => clearInterval(chatPollRef.current);
    }
    return () => clearInterval(chatPollRef.current);
  }, [chatOpen, fetchChatMessages]);

  useEffect(() => {
    if (chatScrollRef.current) chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
  }, [chatMessages]);

  const handleSendChat = async (e) => {
    e.preventDefault();
    const text = chatInput.trim();
    if (!text || chatSending) return;
    setChatSending(true);
    try {
      const token = localStorage.getItem('token');
      const pid = getPartnerId();
      await fetch(`${API_URL}/api/messages`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ receiver_id: pid, content: text })
      });
      setChatInput('');
      await fetchChatMessages();
    } catch { /* ignore */ }
    finally { setChatSending(false); }
  };

  const partnerName = project ? (userType === 'homeowner' ? project.contractor_name : project.homeowner_name) : '';

  const dashboardPath = userType === 'homeowner' ? '/homeowner-dashboard' : '/contractor-dashboard';

  if (loading) {
    return (
      <div className="min-h-screen bg-[#E8E9EB] dark:bg-[#121212] flex items-center justify-center">
        <ArrowsClockwise size={40} weight="bold" className="animate-spin text-[#2B4C6F]" />
      </div>
    );
  }

  if (!project) return null;

  const statusCfg = STATUS_COLORS[project.status] || STATUS_COLORS.planning;
  const isContractor = userType === 'contractor';

  return (
    <div className="min-h-screen bg-[#E8E9EB] dark:bg-[#121212] pb-20" data-testid="project-tracker">
      {/* Header */}
      <div className="bg-[#0F172A] border-b-4 border-black">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3 mb-3">
            <button onClick={() => navigate(dashboardPath)} className="text-neutral-400 hover:text-white" data-testid="back-btn">
              <ArrowLeft size={22} weight="bold" />
            </button>
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="ConstructHelp" className="h-8 w-auto" />
            </Link>
          </div>
          <h1 className="text-xl md:text-2xl font-black text-white uppercase tracking-tight">{project.title}</h1>
          <div className="flex flex-wrap items-center gap-3 mt-2 text-sm">
            <span className={`px-3 py-1 ${statusCfg.light} ${statusCfg.text} text-xs font-bold border`}>{statusCfg.label}</span>
            {project.location && <span className="text-neutral-400 flex items-center gap-1"><MapPin size={14} weight="bold" /> {project.location}</span>}
            {project.budget > 0 && <span className="text-emerald-400 font-bold flex items-center gap-1"><CurrencyDollar size={14} weight="bold" /> ${Number(project.budget).toLocaleString()}</span>}
            {project.timeline && <span className="text-neutral-400 flex items-center gap-1"><Clock size={14} weight="bold" /> {project.timeline}</span>}
          </div>
          <div className="flex items-center gap-2 mt-2 text-xs text-neutral-500">
            <House size={14} weight="bold" /> {project.homeowner_name}
            <span className="mx-1">|</span>
            <Briefcase size={14} weight="bold" /> {project.contractor_name}
          </div>
          {/* Direct Message Button */}
          <button
            onClick={() => setChatOpen(!chatOpen)}
            className="mt-3 h-9 px-4 bg-[#2B4C6F] hover:bg-[#203A55] text-white text-xs font-bold uppercase tracking-wider border border-white/30 flex items-center gap-2 transition-colors"
            data-testid="project-message-btn"
          >
            <ChatCircleDots size={16} weight="bold" />
            Message {partnerName.split(' ')[0]}
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Progress Bar */}
        <div className="bg-white dark:bg-[#0F172A] border-3 border-black shadow-[4px_4px_0px_#000] p-4 mb-6" data-testid="progress-bar">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-bold text-neutral-900 dark:text-white">{project.progress}% Complete</span>
            {(project.status === 'planning' || project.status === 'in_progress' || project.status === 'on_hold') && (
              <button
                onClick={handleStatusToggle}
                className={`px-3 py-1 text-xs font-bold border-2 border-black shadow-[2px_2px_0px_#000] transition-all flex items-center gap-1 ${
                  project.status === 'in_progress'
                    ? 'bg-amber-500 hover:bg-amber-600 text-white'
                    : 'bg-emerald-500 hover:bg-emerald-600 text-white'
                }`}
                data-testid="status-toggle-btn"
              >
                {project.status === 'in_progress' ? <><Pause size={14} weight="bold" /> Pause</> : <><Play size={14} weight="bold" /> Start</>}
              </button>
            )}
          </div>
          <div className="w-full h-4 bg-neutral-200 dark:bg-neutral-700 border border-black overflow-hidden">
            <div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${project.progress}%` }} />
          </div>
          {project.status === 'completed' && (
            <button
              onClick={async () => {
                try {
                  const token = localStorage.getItem('token');
                  const res = await fetch(`${API_URL}/api/certificates/project/${projectId}`, { headers: { Authorization: `Bearer ${token}` } });
                  if (!res.ok) { const err = await res.json(); toast({ title: err.detail || 'Error', variant: 'destructive' }); return; }
                  const blob = await res.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a'); a.href = url; a.download = `Certificate_${project.title?.replace(/\s/g,'_') || 'project'}.pdf`; a.click();
                  URL.revokeObjectURL(url);
                } catch { toast({ title: 'Error generating certificate', variant: 'destructive' }); }
              }}
              className="mt-3 w-full h-10 bg-amber-500 hover:bg-amber-600 text-black font-bold text-sm rounded border-2 border-black shadow-[2px_2px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all flex items-center justify-center gap-2"
              data-testid="download-certificate-btn">
              <DownloadSimple size={16} weight="bold" /> Download Completion Certificate
            </button>
          )}
          <button
            onClick={async () => {
              try {
                const token = localStorage.getItem('token');
                const res = await fetch(`${API_URL}/api/reports/project/${projectId}`, { headers: { Authorization: `Bearer ${token}` } });
                if (!res.ok) { const err = await res.json(); toast({ title: err.detail || 'Error', variant: 'destructive' }); return; }
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a'); a.href = url; a.download = `Project_Report_${project.title?.replace(/\s/g,'_') || 'report'}.pdf`; a.click();
                URL.revokeObjectURL(url);
                toast({ title: 'Report downloaded!' });
              } catch { toast({ title: 'Error generating report', variant: 'destructive' }); }
            }}
            className="mt-2 w-full h-10 bg-[#2B4C6F] hover:bg-[#203A55] text-white font-bold text-sm rounded border-2 border-black shadow-[2px_2px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all flex items-center justify-center gap-2"
            data-testid="export-report-btn">
            <DownloadSimple size={16} weight="bold" /> Export Full Report (PDF)
          </button>
          <button
            onClick={openShare}
            className="mt-2 w-full h-10 bg-black hover:bg-neutral-800 text-white font-bold text-sm rounded border-2 border-[#D95800] shadow-[2px_2px_0px_#D95800] active:translate-y-0.5 active:shadow-none transition-all flex items-center justify-center gap-2"
            data-testid="share-progress-btn">
            <ShareNetwork size={16} weight="bold" className="text-[#D95800]" /> Share Progress with Client
          </button>
        </div>

        {showReviewNudge && !showReviewModal && (
          <div className="fixed bottom-4 right-4 z-40 max-w-sm bg-white dark:bg-[#0F172A] border-4 border-black shadow-[6px_6px_0px_#D95800] p-4" data-testid="review-nudge">
            <button onClick={() => setShowReviewNudge(false)} className="absolute top-2 right-2 text-neutral-400 hover:text-neutral-700" data-testid="review-nudge-close"><X size={16} weight="bold" /></button>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 flex-shrink-0 bg-[#D95800] border-2 border-black flex items-center justify-center">
                <Star size={20} weight="fill" className="text-white" />
              </div>
              <div className="pr-4">
                <p className="font-black text-sm text-neutral-900 dark:text-white">Milestone approved — nice!</p>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 mb-2">
                  Happy with {partnerName}&apos;s work? A quick review helps other homeowners hire with confidence.
                </p>
                <button
                  onClick={() => setShowReviewModal(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#D95800] hover:bg-[#B84A00] text-white text-xs font-black uppercase tracking-wide border-2 border-black shadow-[2px_2px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all"
                  data-testid="review-nudge-cta"
                >
                  <Star size={14} weight="fill" /> Leave a Review
                </button>
              </div>
            </div>
          </div>
        )}

        {showReviewModal && project && (
          <ReviewModal
            contractor={{ contractor_id: project.contractor_id, name: project.contractor_name }}
            currentUserId={userId}
            onClose={() => setShowReviewModal(false)}
            onSubmitted={() => { setShowReviewModal(false); setShowReviewNudge(false); }}
          />
        )}


        {shareOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setShareOpen(false)} data-testid="share-modal">
            <div className="bg-white dark:bg-[#0F172A] border-4 border-black shadow-[8px_8px_0px_#D95800] max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-black uppercase flex items-center gap-2 text-neutral-900 dark:text-white">
                  <ShareNetwork size={20} weight="bold" className="text-[#D95800]" /> Share Progress
                </h3>
                <button onClick={() => setShareOpen(false)} className="p-1 hover:bg-neutral-100 dark:hover:bg-neutral-800" data-testid="share-close-btn"><X size={18} weight="bold" /></button>
              </div>
              <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-3 leading-relaxed">
                Send this read-only link to family or co-owners. They can follow milestones and progress photos — no account or login needed. Budgets and payments stay private.
              </p>
              {sharing ? (
                <div className="h-11 flex items-center justify-center text-sm text-neutral-500"><ArrowsClockwise size={16} className="animate-spin mr-2" /> Generating link…</div>
              ) : (
                <div className="flex items-center gap-2">
                  <div className="flex-1 flex items-center gap-2 border-2 border-black bg-neutral-50 dark:bg-neutral-800 px-3 h-11 overflow-hidden">
                    <LinkSimple size={16} weight="bold" className="text-neutral-400 flex-shrink-0" />
                    <span className="text-xs text-neutral-700 dark:text-neutral-300 truncate" data-testid="share-url">{shareUrl}</span>
                  </div>
                  <button onClick={copyShare} className="h-11 px-4 bg-[#D95800] hover:bg-[#B84A00] text-white font-bold text-sm border-2 border-black shadow-[2px_2px_0px_#000] active:translate-y-0.5 active:shadow-none flex items-center gap-1.5" data-testid="share-copy-btn">
                    <Copy size={16} weight="bold" /> Copy
                  </button>
                </div>
              )}
              {shareUrl && (
                <a href={shareUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-3 text-xs font-bold text-[#2B4C6F] hover:underline" data-testid="share-preview-link">
                  Preview client view <ArrowLeft size={12} weight="bold" className="rotate-180" />
                </a>
              )}
            </div>
          </div>
        )}

        {/* Escrow / Payment Panel */}
        <div className="bg-white dark:bg-[#0F172A] border-3 border-black shadow-[4px_4px_0px_#000] p-4 mb-6" data-testid="escrow-panel">
          {project.escrow_status === 'funded' ? (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-black uppercase text-neutral-900 dark:text-white flex items-center gap-2">
                  <ShieldCheck size={18} weight="bold" className="text-emerald-500" /> Escrow Active
                </h3>
                <span className="px-2 py-0.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-xs font-bold border border-emerald-300 dark:border-emerald-700">Funded</span>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 p-3 text-center">
                  <p className="text-[10px] font-bold uppercase text-neutral-500">Total</p>
                  <p className="text-lg font-black text-neutral-900 dark:text-white">${Number(project.escrow_total || 0).toLocaleString()}</p>
                </div>
                <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700 p-3 text-center">
                  <p className="text-[10px] font-bold uppercase text-emerald-600">Released</p>
                  <p className="text-lg font-black text-emerald-600">${Number(project.escrow_released || 0).toLocaleString()}</p>
                </div>
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 p-3 text-center">
                  <p className="text-[10px] font-bold uppercase text-amber-600">Held</p>
                  <p className="text-lg font-black text-amber-600">${Number(project.escrow_held || 0).toLocaleString()}</p>
                </div>
              </div>
              {isContractor && (
                <p className="text-xs text-neutral-500 mt-2 flex items-center gap-1">
                  <Wallet size={14} weight="bold" /> Payments release to your wallet when the homeowner approves each completed milestone. Use your wallet to pay workers.
                </p>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 border-2 border-amber-300 dark:border-amber-700 flex items-center justify-center">
                  <CreditCard size={20} weight="bold" className="text-amber-600" />
                </div>
                <div>
                  <p className="font-bold text-neutral-900 dark:text-white text-sm">Escrow Not Funded</p>
                  <p className="text-xs text-neutral-500">
                    {userType === 'homeowner'
                      ? `Pre-fund $${Number(project.budget).toLocaleString()} so payments release when you approve milestones`
                      : `Waiting for ${project.homeowner_name} to fund the escrow`}
                  </p>
                </div>
              </div>
              {userType === 'homeowner' && (
                <button
                  onClick={handleFundProject}
                  disabled={funding}
                  className="h-10 px-5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold uppercase tracking-wider border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] disabled:opacity-40 flex items-center gap-2 transition-all"
                  data-testid="fund-project-btn"
                >
                  {funding ? <ArrowsClockwise size={16} weight="bold" className="animate-spin" /> : <ShieldCheck size={16} weight="bold" />}
                  Fund Project
                </button>
              )}
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-5 gap-6">
          {/* Left: Milestones */}
          <div className="lg:col-span-2">
            <h2 className="text-sm font-black uppercase text-neutral-900 dark:text-white mb-3 flex items-center gap-2">
              <Flag size={18} weight="bold" className="text-[#2B4C6F]" /> Milestones
            </h2>
            <div className="space-y-0" data-testid="milestones-list">
              {milestones.map((m, idx) => {
                const msCfg = MS_STATUS[m.status] || MS_STATUS.pending;
                const Icon = msCfg.icon;
                const isLast = idx === milestones.length - 1;
                return (
                  <div key={m.id} className="flex gap-3" data-testid={`milestone-${m.id}`}>
                    {/* Timeline line */}
                    <div className="flex flex-col items-center">
                      <button
                        onClick={() => handleToggleMilestone(m.id, m.status)}
                        className={`w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-full border-2 border-black transition-colors ${
                          m.status === 'completed' ? 'bg-emerald-500' : m.status === 'in_progress' ? 'bg-amber-500' : 'bg-white dark:bg-neutral-800'
                        }`}
                        data-testid={`milestone-toggle-${m.id}`}
                      >
                        <Icon size={16} weight="bold" className={m.status === 'pending' ? msCfg.color : 'text-white'} />
                      </button>
                      {!isLast && <div className={`w-0.5 flex-1 min-h-[24px] ${msCfg.line}`} />}
                    </div>
                    {/* Content */}
                    <div className="pb-4 flex-1 min-w-0">
                      <p className={`text-sm font-bold ${m.status === 'completed' ? 'text-emerald-700 dark:text-emerald-400 line-through' : 'text-neutral-900 dark:text-white'}`}>
                        {m.title}
                      </p>
                      {m.description && <p className="text-xs text-neutral-500 mt-0.5">{m.description}</p>}
                      {m.due_date && <p className="text-[10px] text-neutral-400 mt-0.5 flex items-center gap-1"><Clock size={10} weight="bold" /> {m.due_date}</p>}
                      {m.amount > 0 && (
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className={`text-[10px] font-bold ${m.payment_released ? 'text-emerald-600' : 'text-neutral-500'}`}>
                            ${Number(m.amount).toLocaleString()}
                          </span>
                          {m.payment_released && (
                            <span className="text-[10px] text-emerald-500 flex items-center gap-0.5">
                              <CheckCircle size={10} weight="bold" /> Paid
                            </span>
                          )}
                          {!m.payment_released && project.escrow_status === 'funded' && m.status !== 'completed' && (
                            <span className="text-[10px] text-amber-500">Held in escrow</span>
                          )}
                          {!m.payment_released && project.escrow_status === 'funded' && m.status === 'completed' && !m.approved && (
                            <span className="text-[10px] text-amber-600">Releases on approval</span>
                          )}
                        </div>
                      )}
                      {m.status === 'completed' && (
                        m.approved ? (
                          <span
                            className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 bg-emerald-500 text-white text-[10px] font-black uppercase tracking-wide border-2 border-black"
                            data-testid={`milestone-approved-badge-${m.id}`}
                            title={m.approved_at ? `Approved ${new Date(m.approved_at).toLocaleString()}` : ''}
                          >
                            <SealCheck size={12} weight="fill" /> Approved by {m.approved_by_name}
                          </span>
                        ) : userType === 'homeowner' ? (
                          <button
                            onClick={() => approveMilestone(m.id)}
                            className="inline-flex items-center gap-1 mt-1.5 px-2.5 py-1 bg-white dark:bg-neutral-800 text-emerald-600 text-[10px] font-black uppercase tracking-wide border-2 border-emerald-600 shadow-[2px_2px_0px_#059669] hover:bg-emerald-50 dark:hover:bg-emerald-900/20 active:translate-y-0.5 active:shadow-none transition-all"
                            data-testid={`milestone-approve-btn-${m.id}`}
                          >
                            <SealCheck size={12} weight="bold" /> Approve
                          </button>
                        ) : (
                          <span className="block mt-1 text-[10px] text-neutral-400 italic">Awaiting homeowner approval</span>
                        )
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right: Updates Timeline */}
          <div className="lg:col-span-3">
            <h2 className="text-sm font-black uppercase text-neutral-900 dark:text-white mb-3 flex items-center gap-2">
              <ChatCircleDots size={18} weight="bold" className="text-[#D95800]" /> Updates
            </h2>

            {/* Post Update */}
            <div className="bg-white dark:bg-[#0F172A] border-3 border-black shadow-[4px_4px_0px_#2B4C6F] p-3 mb-4" data-testid="post-update-form">
              <textarea
                value={newUpdate}
                onChange={(e) => setNewUpdate(e.target.value)}
                placeholder="Post a progress update..."
                rows={2}
                className="w-full px-3 py-2 text-sm bg-neutral-50 dark:bg-neutral-800 border-2 border-black dark:border-neutral-600 text-neutral-900 dark:text-white placeholder-neutral-400 resize-none focus:outline-none focus:border-[#2B4C6F]"
                data-testid="update-input"
              />
              {photoPreview && (
                <div className="relative mt-2 inline-block">
                  <img src={photoPreview} alt="" className="h-20 rounded border-2 border-black" />
                  <button onClick={() => { setPhotoPreview(null); setPhotoData(null); }} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white flex items-center justify-center rounded-full border border-black">
                    <X size={10} weight="bold" />
                  </button>
                </div>
              )}
              <div className="flex items-center justify-between mt-2">
                <label className="flex items-center gap-1 px-3 py-1.5 bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700 text-neutral-600 dark:text-neutral-400 text-xs font-bold border border-neutral-300 dark:border-neutral-600 cursor-pointer transition-colors">
                  <Camera size={16} weight="bold" /> Photo
                  <input type="file" accept="image/*" className="hidden" onChange={handlePhotoSelect} />
                </label>
                <button
                  onClick={handlePostUpdate}
                  disabled={!newUpdate.trim() || posting}
                  className="h-8 px-4 bg-[#2B4C6F] hover:bg-[#203A55] text-white text-xs font-bold border-2 border-black shadow-[2px_2px_0px_#000] disabled:opacity-40 flex items-center gap-1 transition-all"
                  data-testid="post-update-btn"
                >
                  {posting ? <ArrowsClockwise size={14} weight="bold" className="animate-spin" /> : <PaperPlaneTilt size={14} weight="bold" />}
                  Post
                </button>
              </div>
            </div>

            {/* Updates Feed */}
            {updates.length === 0 ? (
              <div className="text-center py-8">
                <ChatCircleDots size={40} weight="bold" className="text-neutral-300 dark:text-neutral-600 mx-auto mb-2" />
                <p className="text-sm text-neutral-500 font-bold">No updates yet</p>
                <p className="text-xs text-neutral-400">Post the first progress update above</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="updates-feed">
                {updates.map((u) => {
                  const isMine = u.author_id === userId;
                  const isSystem = u.is_system;
                  return (
                    <div key={u.id} className={`border-2 border-black p-3 ${isSystem ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-300 dark:border-emerald-700' : 'bg-white dark:bg-[#0F172A]'}`} data-testid={`update-${u.id}`}>
                      <div className="flex items-center gap-2 mb-1.5">
                        <div className={`w-6 h-6 flex items-center justify-center flex-shrink-0 border border-black ${
                          u.author_type === 'contractor' ? 'bg-[#2B4C6F]' : u.author_type === 'homeowner' ? 'bg-[#D95800]' : 'bg-emerald-500'
                        }`}>
                          {u.author_type === 'contractor' ? <Briefcase size={12} weight="bold" className="text-white" /> :
                           u.author_type === 'homeowner' ? <House size={12} weight="bold" className="text-white" /> :
                           <CheckCircle size={12} weight="bold" className="text-white" />}
                        </div>
                        <span className="text-xs font-bold text-neutral-900 dark:text-white">{u.author_name}</span>
                        <span className="text-[10px] text-neutral-400">{new Date(u.created_at).toLocaleDateString()} {new Date(u.created_at).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}</span>
                      </div>
                      <p className="text-sm text-neutral-700 dark:text-neutral-300 leading-relaxed">{u.message}</p>
                      {u.photo_url && (
                        <div className="mt-2">
                          <img src={u.photo_url} alt="" className="max-h-48 rounded border-2 border-black cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => {
                              const allPhotos = updates.filter(up => up.photo_url).map(up => up.photo_url);
                              const idx = allPhotos.indexOf(u.photo_url);
                              setLightboxImages(allPhotos);
                              setLightboxIndex(Math.max(0, idx));
                            }}
                            data-testid={`update-photo-${u.id}`} />
                        </div>
                      )}
                      {!isSystem && (
                        <div className="mt-2 flex items-center gap-2">
                          <button
                            onClick={() => toggleLove(u.id)}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 border-2 border-black text-xs font-black uppercase tracking-wide transition-all active:translate-y-0.5 ${
                              u.loved ? 'bg-[#D95800] text-white shadow-[2px_2px_0px_#000]' : 'bg-white dark:bg-neutral-800 text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-700'
                            }`}
                            data-testid={`love-btn-${u.id}`}
                            title={u.loved ? 'Remove love' : 'Love this update'}
                          >
                            <Heart size={14} weight={u.loved ? 'fill' : 'bold'} />
                            {u.loved_count > 0 && <span data-testid={`love-count-${u.id}`}>{u.loved_count}</span>}
                          </button>
                          <button
                            onClick={() => setOpenThread(openThread === u.id ? null : u.id)}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 border-2 border-black text-xs font-black uppercase tracking-wide bg-white dark:bg-neutral-800 text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-all active:translate-y-0.5"
                            data-testid={`comment-btn-${u.id}`}
                          >
                            <ChatCircle size={14} weight="bold" /> Comment
                            {(u.comments?.length || 0) > 0 && <span>· {u.comments.length}</span>}
                          </button>
                        </div>
                      )}
                      {!isSystem && (openThread === u.id || (u.comments?.length || 0) > 0) && (
                        <div className="mt-2 border-t border-neutral-200 dark:border-neutral-700 pt-2 space-y-1.5" data-testid={`thread-${u.id}`}>
                          {(u.comments || []).map((c) => (
                            <div key={c.id} className="text-sm">
                              <span className={`font-black ${c.author_type === 'contractor' ? 'text-[#2B4C6F]' : c.author_type === 'homeowner' ? 'text-[#D95800]' : 'text-neutral-700 dark:text-neutral-300'}`}>{c.author_name}</span>
                              <span className="text-neutral-700 dark:text-neutral-300"> {c.text}</span>
                            </div>
                          ))}
                          {openThread === u.id && (
                            <div className="flex items-center gap-2 pt-1">
                              <input
                                value={commentInputs[u.id] || ''}
                                onChange={(e) => setCommentInputs((p) => ({ ...p, [u.id]: e.target.value }))}
                                onKeyDown={(e) => { if (e.key === 'Enter') submitComment(u.id); }}
                                placeholder="Add a comment…"
                                className="flex-1 h-9 px-2 border-2 border-black bg-white dark:bg-neutral-800 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#2B4C6F]"
                                data-testid={`comment-input-${u.id}`}
                              />
                              <button
                                onClick={() => submitComment(u.id)}
                                className="h-9 px-3 bg-[#2B4C6F] hover:bg-[#203A55] text-white border-2 border-black shadow-[2px_2px_0px_#000] active:translate-y-0.5 active:shadow-none flex items-center"
                                data-testid={`comment-send-${u.id}`}
                              >
                                <PaperPlaneTilt size={14} weight="bold" />
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Chat Sidebar */}
      {chatOpen && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="project-chat-sidebar">
          <div className="absolute inset-0 bg-black/40" onClick={() => setChatOpen(false)} />
          <div className="relative w-full max-w-sm bg-white dark:bg-[#1A1A1A] border-l-4 border-black shadow-[-8px_0_20px_rgba(0,0,0,0.3)] flex flex-col h-full" style={{ animation: 'slideIn 0.25s ease-out' }}>
            {/* Header */}
            <div className="flex items-center justify-between p-3 bg-[#2B4C6F] border-b-2 border-black flex-shrink-0">
              <div className="flex items-center gap-2">
                <ChatCircleDots size={20} weight="bold" className="text-white" />
                <h3 className="text-white font-black uppercase text-sm tracking-wider">{partnerName}</h3>
              </div>
              <button onClick={() => setChatOpen(false)} className="text-white/80 hover:text-white" data-testid="chat-sidebar-close">
                <X size={20} weight="bold" />
              </button>
            </div>
            {/* Messages */}
            <div ref={chatScrollRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-neutral-100 dark:bg-neutral-900/80">
              {chatMessages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center px-4">
                  <ChatCircleDots size={40} weight="bold" className="text-neutral-300 dark:text-neutral-600 mb-2" />
                  <p className="text-sm text-neutral-500 font-bold">Start a conversation</p>
                  <p className="text-xs text-neutral-400 mt-1">Messages are private between you and {partnerName}</p>
                </div>
              ) : chatMessages.map((msg) => {
                const isMine = msg.sender_id === userId;
                return (
                  <div key={msg._id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] px-3 py-2 text-sm ${isMine ? 'bg-[#2B4C6F] text-white border border-[#203A55]' : 'bg-white dark:bg-[#0F172A] text-neutral-900 dark:text-white border border-neutral-300 dark:border-neutral-700'}`}>
                      <p className="break-words leading-relaxed">{msg.content}</p>
                      <p className={`text-[10px] mt-1 ${isMine ? 'text-white/60' : 'text-neutral-400'}`}>
                        {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Input */}
            <form onSubmit={handleSendChat} className="flex items-center gap-2 p-3 bg-white dark:bg-[#0F172A] border-t-2 border-black flex-shrink-0">
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                placeholder="Type a message..."
                className="flex-1 h-10 px-3 text-sm bg-neutral-100 dark:bg-neutral-800 border-2 border-black dark:border-neutral-600 text-neutral-900 dark:text-white placeholder-neutral-400 focus:outline-none focus:border-[#2B4C6F]"
                data-testid="project-chat-input"
              />
              <button type="submit" disabled={!chatInput.trim() || chatSending} className="h-10 w-10 flex items-center justify-center bg-[#2B4C6F] hover:bg-[#203A55] text-white border-2 border-black shadow-[2px_2px_0px_#000] disabled:opacity-40 transition-all flex-shrink-0" data-testid="project-chat-send">
                <PaperPlaneTilt size={18} weight="bold" />
              </button>
            </form>
          </div>
        </div>
      )}

      <style>{`@keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>

      {lightboxImages && (
        <Lightbox images={lightboxImages} initialIndex={lightboxIndex} onClose={() => setLightboxImages(null)} />
      )}
    </div>
  );
}
